import { useEffect, useState } from "react";
import { Progress, Input, Button, message} from "antd";
import {
  Heart,
  Dumbbell,
  Smile,
  Target,
  BicepsFlexed,
  StretchHorizontal,
  Scale,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { useLoading } from "../../components/LoadingContext";
import { openNotification } from "../../components/notifications";
import { fetchTamagotchi, fetchTamagotchiStatus, fetchTamagotchiLevel, patchTamagotchi } from "../../apis/tamagotchiApi";
import HowItWorks from "../../components/howItWorks/HowItWorks";

const statConfig = [
  { key: "strength", label: "Fuerza", icon: <BicepsFlexed color="#facc15" />, strokeColor: "#facc15" },
  { key: "endurance", label: "Resistencia", icon: <Heart color="#ec4899" />, strokeColor: "#ec4899" },
  { key: "power", label: "Poder", icon: <Dumbbell color="#facc15" />, strokeColor: "#facc15" },
  { key: "fun", label: "Diversión", icon: <Smile color="#4ade80" />, strokeColor: "#4ade80" },
  { key: "flexibility", label: "Flexibilidad", icon: <StretchHorizontal color="#38bdf8" />, strokeColor: "#38bdf8" },
  { key: "coordination", label: "Coordinación", icon: <Target color="#c084fc" />, strokeColor: "#c084fc" },
  { key: "balance", label: "Equilibrio", icon: <Scale color="#fbbf24" />, strokeColor: "#fbbf24" },
];

const Tamagotchi = () => {
  const { user_id } = useParams();  
  const [tamagotchi, setTamagotchi] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [nextLevel, setNextLevel] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [saving, setSaving] = useState(false);
  const { setLoading } = useLoading();
  const [media, setMedia] = useState({ type: "image", src: "/img/tamagotchi/egg.png" });


  useEffect(() => {
    const loadTamagotchi = async () => {
      setLoading(true);
      try {
        let response = await fetchTamagotchi(user_id);
        const tamagotchiResponse = response.data[0];
        if(tamagotchiResponse === undefined) {
          message.error("Por favor bautiza tu tamagotchi!");
          setLoading(false);
          return;
        }
        setTamagotchi(tamagotchiResponse);

        response = await fetchTamagotchiLevel(tamagotchiResponse.level);
        const currentLevelResponse = response.data[0];
        setCurrentLevel(currentLevelResponse);

        const nextLevelResponse = await fetchTamagotchiLevel(tamagotchiResponse.level + 1);
        setNextLevel(nextLevelResponse.data[0]);

        response = await fetchTamagotchiStatus(tamagotchiResponse);
        const status = response.data[0];

        const baseName = `${currentLevelResponse.image_prefix}_${tamagotchiResponse.type}_${status.status}`;
        const videoPath = `/img/tamagotchi/${baseName}.mp4`;
        const imagePath = `/img/tamagotchi/${baseName}.png`;
        const fallbackImagePath = `/img/tamagotchi/${currentLevelResponse.image_prefix}.png`;

        const checkFileExists = async (url, expectedType) => {
          try {
            const res = await fetch(url, { method: "HEAD" });
            if (!res.ok) return false;

            const contentType = res.headers.get("Content-Type");
            if (!contentType) return false;

            // Confirm it's a video file (for .mp4, typically video/quicktime or video/mp4 depending on server)
            return contentType.startsWith(expectedType);
          } catch {
            return false;
          }
        };


        if (await checkFileExists(videoPath, "video")) {
          setMedia({ type: "video", src: videoPath });
        } else if (await checkFileExists(imagePath, "image")) {
          setMedia({ type: "image", src: imagePath });
        } else {
          setMedia({ type: "image", src: fallbackImagePath });
        }
      } catch (error) {
        message.error("Error al cargar la info de tu mascota, intenta más tarde: " + error);
      }
      setLoading(false);
    };

    loadTamagotchi();
  }, [user_id, setLoading]);

  const handleSetName = async () => {
    if (!nameInput.trim()) {
      message.error("Please enter a name.");
      return;
    }
    setSaving(true);
    // Send all values, updating the name
    const params = { ...tamagotchi, name: nameInput };
    const response = await patchTamagotchi(user_id, params);
    if (response.success) {
      setTamagotchi(prev => ({ ...prev, name: nameInput }));
    } else {
      openNotification('error', 'Error al bautizar a tu mascota, intenta más tarde');
    }
    setSaving(false);
  };

  return (
    <div className="container mt-4 p-4 bg-white shadow rounded-4" style={{ maxWidth: 480, position: "relative" }}>
      {/* Badge with question mark */}
      <HowItWorks title={"¿Cómo funciona tu Tamagotchi?"}>
        <ul>
          <li>Para empezar, dale un nombre lindo a tu mascota.</li>
          <li>Al marcar tu asistencia en las actividades, subirán diferentes estadísticas:</li>
          <ul>
            <li>Fuerza: Functional, Tonic, Box</li>
            <li>Resistencia: Tonic, Step, Functional, Combat</li>
            <li>Poder: Combat, Box, Functional</li>
            <li>Diversión: Dance, Pound, Flex</li>
            <li>Flexibilidad: Flex, Dance</li>
            <li>Coordinación: Flyr, Jump, Pound, Step</li>
            <li>Equilibrio: Flyr, Jump</li>
          </ul>
          <li>Cada clase además te dará experiencia para tu mascota suba de nivel!</li>
          <li>Cuida a tu mascota para que esté feliz y saludable, porque si todas sus estadísticas llegan a 0, muere <span role="img" aria-label="triste">😢</span>.</li>
        </ul>
      </HowItWorks>    
      <div className="text-center mb-4">
        {media.type === "video" ? (
          <video
            src={media.src}
            width={160}
            height={160}
            autoPlay
            muted
            playsInline
          />
        ) : (
          <img
            src={media.src}
            alt="Tamagotchi Avatar"
            style={{ width: 160, height: 160 }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `/img/tamagotchi/egg.png`; // optional second fallback
            }}
          />
        )}
      </div>
      <div className="mb-3 text-center">
        {tamagotchi?.name ? (
          <>
            <h3>{tamagotchi.name}</h3>
          </>
        ) : (
          <div style={{ maxWidth: 300, margin: "0 auto" }}>
            <Input
              placeholder="Dale un nombre a tu mascota"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              maxLength={32}
              disabled={saving}
              onPressEnter={handleSetName}
            />
            <Button
              type="primary"
              className="mt-2"
              loading={saving}
              onClick={handleSetName}
              disabled={!nameInput.trim()}
              block
            >
              Bautizar
            </Button>
          </div>
        )}
        <div className="w-75 m-auto">
          <Progress
            percent={tamagotchi && nextLevel && currentLevel ? (tamagotchi["experience"] - currentLevel.experience) * 100 / (nextLevel.experience - currentLevel.experience) : 0}
            showInfo={false}
            strokeColor={"black"}
          />
        </div>
        <h5 className="my-2">Nivel: {tamagotchi?.level}</h5>
      </div>
      {statConfig.map((stat, idx) => (
        <div key={idx} className="mb-3">
          <div className="d-flex align-items-center gap-2 mb-1">
            {stat.icon}
            <span className="fw-semibold">{stat.label}: {tamagotchi ? tamagotchi[stat.key] : 0 }</span>
          </div>
          <Progress
            percent={tamagotchi ? tamagotchi[stat.key] || 0 : 0}
            showInfo={false}
            strokeColor={stat.strokeColor}
          />
        </div>
      ))}
    </div>
  );
};

export default Tamagotchi;